/** 발주서 변환 페이지 — 상위 컨트롤러.
 *
 *  - 대상(파트너/상위 그룹) 선택 → 파일 업로드 → /api/convert POST → 변환 결과 표시.
 *  - ReviewPanel 에서 결과 검토 + 인라인 매핑/단가 등록 후 /api/convert/commit 으로 최종 저장.
 *
 *  하위 모듈:
 *    - types.ts:              공용 타입/상수 (STATUS_INFO 등)
 *    - ReviewPanel.tsx:       변환 결과 검토 UI (미매핑 부서/품목/단가 알림 포함)
 *    - MissingItemsMapper.tsx: 미매칭 품목 → ItemMapping 인라인 등록
 *    - MissingPricesMapper.tsx: 미등록 단가 → PartnerPrice 인라인 등록
 */
import { useRef, useState } from 'react'
import { ChevronRight, Download, Loader2, UploadCloud, X } from 'lucide-react'

import { api } from '@/api/client'
import { useAuth } from '@/stores/auth'
import { useCurrentCompany } from '@/lib/useCompany'
import { useToast } from '@/stores/toast'
import { useConvertDraft } from '@/stores/convertDraft'
import { GroupPicker } from '@/components/GroupPicker'
import { cn, formatNum } from '@/lib/utils'

import { ReviewPanel } from './ReviewPanel'
import {
  STATUS_INFO,
  type CommitResult, type ConvertResult, type ItemRow,
} from './types'

export function ConvertTab() {
  const push = useToast((s) => s.push)
  const { token } = useAuth()
  const company = useCurrentCompany()

  // 탭 이동(코드매칭·부서매칭)해도 유지되는 업로드/변환/검토 상태 — 인메모리 스토어.
  // ConvertTab 이 언마운트돼도 스토어가 값을 보관 → 돌아오면 처음 상태 그대로.
  const {
    groupId, setGroupId, groupLabel, setGroupLabel,
    files, setFiles, mergeSameDay, setMergeSameDay,
    result, setResult, expanded, setExpanded,
    partnerRows, setPartnerRows, itemRows, setItemRows,
    priceRows, setPriceRows, orderRows, setOrderRows,
    committed, setCommitted, reset: resetDraft,
  } = useConvertDraft()

  // 전환용(휘발성) 로컬 상태 — 초기화돼도 무방
  const [dragging, setDragging] = useState(false)
  const [loading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [committing, setCommitting] = useState(false)
  const picker = useRef<HTMLInputElement>(null)

  // 거래처별 그룹화 (품목 섹션용; 신규 먼저 + 한글순)
  const itemsByPartner = itemRows.reduce<
    Map<string, { is_new: boolean; rows: Array<{ row: ItemRow; idx: number }> }>
  >((acc, row, idx) => {
    const key = row.partner_name || '(업장 미상)'
    if (!acc.has(key)) acc.set(key, { is_new: row.is_new_partner, rows: [] })
    acc.get(key)!.rows.push({ row, idx })
    return acc
  }, new Map())
  const itemGroups = Array.from(itemsByPartner.entries()).sort((a, b) => {
    if (a[1].is_new !== b[1].is_new) return a[1].is_new ? -1 : 1
    return a[0].localeCompare(b[0], 'ko')
  })

  function addFiles(list: FileList | File[]) {
    // FileList → Array 즉시 변환 후 push (input.value=''로 인한 race 방지)
    const incoming = Array.from(list)
    if (!incoming.length) return
    setFiles((cur) => {
      const next = [...cur]
      for (const f of incoming) {
        if (!next.some((x) => x.name === f.name && x.size === f.size)) next.push(f)
      }
      return next
    })
  }
  function removeFile(i: number) { setFiles((cur) => cur.filter((_, idx) => idx !== i)) }
  function reset() {
    resetDraft()
    if (picker.current) picker.current.value = ''
  }

  const targetId = groupId

  /** ① 섹션에서 거래처 이름 바뀌면 ②③④의 partner_name도 함께 변경 */
  function renamePartner(oldName: string, newName: string) {
    setPartnerRows((cur) => cur.map((r) => r.name === oldName ? { ...r, name: newName } : r))
    if (oldName === newName) return
    setItemRows((cur) => cur.map((r) => r.partner_name === oldName ? { ...r, partner_name: newName } : r))
    setPriceRows((cur) => cur.map((r) => r.partner_name === oldName ? { ...r, partner_name: newName } : r))
    setOrderRows((cur) => cur.map((r) => r.partner_name === oldName ? { ...r, partner_name: newName } : r))
  }

  /** ② 섹션에서 품목명 바뀌면 ③단가·④출고 라인의 item_name 도 함께 변경. */
  function renameItem(partnerName: string, oldItemName: string, newItemName: string) {
    setItemRows((cur) => cur.map((r) =>
      r.partner_name === partnerName && r.name === oldItemName
        ? { ...r, name: newItemName } : r,
    ))
    if (oldItemName === newItemName) return
    setPriceRows((cur) => cur.map((r) =>
      r.partner_name === partnerName && r.item_name === oldItemName
        ? { ...r, item_name: newItemName } : r,
    ))
    setOrderRows((cur) => cur.map((order) =>
      order.partner_name !== partnerName ? order : {
        ...order,
        lines: order.lines.map((l) =>
          l.item_name === oldItemName ? { ...l, item_name: newItemName } : l,
        ),
      },
    ))
  }

  /** ② 섹션에서 단위 바뀌면 ④출고 라인의 unit도 함께 변경 */
  function changeItemUnit(partnerName: string, itemName: string, newUnit: string) {
    setItemRows((cur) => cur.map((r) =>
      r.partner_name === partnerName && r.name === itemName
        ? { ...r, unit: newUnit } : r,
    ))
    setOrderRows((cur) => cur.map((order) =>
      order.partner_name !== partnerName ? order : {
        ...order,
        lines: order.lines.map((l) =>
          l.item_name === itemName ? { ...l, unit: newUnit } : l,
        ),
      },
    ))
  }

  // 더블클릭으로 커밋이 두 번 가지 않게 — 버튼 비활성화(렌더 후) 전에도 막는 즉시 잠금.
  const committingRef = useRef(false)

  async function commitAll() {
    if (committingRef.current) return
    const orders = orderRows.filter((r) => r.include && r.lines.length)
    if (!orders.length) { push('추가할 출고전표가 없습니다', 'warn'); return }

    committingRef.current = true
    setCommitting(true)
    try {
      const res = await api<CommitResult>('/api/convert/commit', {
        method: 'POST',
        body: {
          orders: orders.map((r) => ({
            partner_name: r.partner_name, order_date: r.order_date,
            note: r.note, lines: r.lines,
          })),
        },
      })
      setCommitted(res)
      const skipLines = res.skipped.lines_no_item + res.skipped.lines_no_price
      const dups = res.skipped.duplicates ?? 0
      push(
        `출고전표 ${res.created.orders}건 등록`
        + (skipLines ? ` (라인 ${skipLines}건 skip)` : '')
        + (dups ? ` · 이미 같은 내용으로 등록된 전표 ${dups}건은 건너뜀` : ''),
        dups && !res.created.orders ? 'warn' : 'success'
      )
      if (res.skipped.closed) {
        push(`일일매출 마감된 거래처라 ${res.skipped.closed}건은 저장하지 않았습니다: `
          + (res.closed_messages ?? []).slice(0, 3).join(' / '), 'warn', 10000)
      }
    } catch (err) {
      push(`추가 실패: ${err instanceof Error ? err.message : err}`, 'error', 6000)
    } finally {
      committingRef.current = false
      setCommitting(false)
    }
  }

  function upload() {
    return new Promise<ConvertResult>((resolve, reject) => {
      const fd = new FormData()
      fd.append('target_type', 'group')
      if (targetId) fd.append('target_id', String(targetId))
      fd.append('merge', mergeSameDay ? '1' : '0')
      files.forEach((f) => fd.append('files', f, f.name))
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) setProgress(Math.round((e.loaded / e.total) * 100))
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try { resolve(JSON.parse(xhr.responseText)) } catch { reject(new Error('응답 파싱 실패')) }
        } else if (xhr.status === 401) reject(new Error('인증 만료'))
        else reject(new Error(`HTTP ${xhr.status}`))
      }
      xhr.onerror = () => reject(new Error('네트워크 오류'))
      xhr.open('POST', '/api/convert')
      if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      if (company) xhr.setRequestHeader('X-Company-Id', String(company.id))
      xhr.send(fd)
    })
  }

  async function convert() {
    if (!files.length) return
    if (!targetId) { push('상위 거래처를 먼저 선택하세요', 'warn'); return }
    setLoading(true); setResult(null); setProgress(0)
    try {
      const data = await upload()
      setResult(data)
      // 변환 결과로 4개 검토 섹션 상태를 파생(예전 useEffect[result] 자리).
      // 스토어 기반이라 탭 이동 후 돌아와도 유지되고, 새 변환 시에만 갱신된다.
      setPartnerRows((data.new_partner_candidates ?? []).map((c) => ({ ...c, include: true })))
      setItemRows((data.new_item_candidates ?? []).map((c) => ({ ...c, include: true })))
      setPriceRows((data.new_price_candidates ?? []).map((c) => ({ ...c, include: true })))
      setOrderRows((data.new_order_candidates ?? []).map((c) => ({ ...c, include: true })))
      setCommitted(null)
      const ok = data.summary.filter((r) => r.status === 'ok').length
      const skipped = data.summary.length - ok
      if (data.total_rows > 0) push(`${ok}개 파일에서 ${data.total_rows}행 변환 완료`, 'success')
      else push('변환된 행이 없습니다', 'warn')
      if (skipped > 0) push(`${skipped}개 파일이 처리되지 않았습니다`, 'warn', 5000)
      const parts: string[] = []
      if (data.new_partner_candidates.length) parts.push(`거래처 ${data.new_partner_candidates.length}`)
      if (data.new_item_candidates.length) parts.push(`품목 ${data.new_item_candidates.length}`)
      if (data.new_price_candidates.length) parts.push(`단가 ${data.new_price_candidates.length}`)
      if (data.new_order_candidates.length) parts.push(`출고 ${data.new_order_candidates.length}`)
      if (parts.length) {
        push(`신규 후보 — ${parts.join(' / ')}건. 검토 후 추가하세요`, 'info', 6000)
      }
    } catch (err) {
      push(`오류: ${err instanceof Error ? err.message : err}`, 'error', 6000)
    } finally { setLoading(false) }
  }

  const okFiles = result?.summary.filter((r) => r.status === 'ok') ?? []
  const downloadUrl = result?.download_id ? `/api/convert/download/${result.download_id}` : ''

  function toggle(i: number) {
    setExpanded((cur) => {
      const next = new Set(cur)
      if (next.has(i)) next.delete(i); else next.add(i)
      return next
    })
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* 상위 거래처 선택 (필수) */}
      <div className="mb-4 bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
            상위 거래처 *
          </label>
          <GroupPicker
            value={groupId}
            onChange={(g) => { setGroupId(g.id); setGroupLabel(`${g.name} · ${g.member_count}개`) }}
            placeholder={groupLabel || '상위 거래처 선택 (필수)'}
          />
          {groupId && (
            <p className="text-[11px] text-blue-700 mt-1.5">
              파일별 부서/창고/property 정보로 자식 매출처에 자동 라우팅됩니다.
            </p>
          )}
        </div>
      </div>

      <div
        role="button" tabIndex={targetId ? 0 : -1}
        onClick={() => targetId && picker.current?.click()}
        onKeyDown={(e) => { if (targetId && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); picker.current?.click() } }}
        className={cn(
          'block rounded-2xl border-2 border-dashed px-8 py-12 text-center bg-white transition-colors focus:outline-none',
          !targetId
            ? 'border-slate-200 opacity-50 cursor-not-allowed'
            : dragging
              ? 'border-blue-500 bg-blue-50 cursor-pointer'
              : 'border-slate-300 hover:border-blue-400 focus:border-blue-500 cursor-pointer',
        )}
        onDrop={(e) => { e.preventDefault(); setDragging(false); if (targetId) addFiles(e.dataTransfer.files) }}
        onDragOver={(e) => { e.preventDefault(); if (targetId) setDragging(true) }}
        onDragLeave={(e) => { e.preventDefault(); setDragging(false) }}
      >
        <UploadCloud className="mx-auto w-12 h-12 text-slate-400" />
        <p className="mt-3 text-base font-semibold text-slate-700">
          {targetId ? '여기로 드래그하거나 클릭해서 선택' : '상위 거래처를 먼저 선택하세요'}
        </p>
        <p className="text-sm text-slate-500 mt-1">여러 파일을 한 번에 올릴 수 있습니다.</p>
        <input
          ref={picker} type="file" multiple
          style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
          accept=".xlsx,.xls,.html,.pdf,.jpg,.jpeg,.png"
          onChange={(e) => {
            const list = e.target.files
            if (list && list.length) addFiles(list)
            e.target.value = ''
          }}
        />
      </div>

      {files.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={f.name + f.size} className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-lg pl-3 pr-2 py-1.5 text-sm shadow-sm">
              <span className="font-medium text-slate-700 truncate max-w-[200px]">{f.name}</span>
              <span className="text-xs text-slate-400 tabular-nums">{(f.size / 1024).toFixed(0)} KB</span>
              <button onClick={() => removeFile(i)} disabled={loading} className="text-slate-400 hover:text-rose-500 disabled:opacity-30 p-0.5 rounded">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 flex items-center gap-3">
        <button onClick={convert} disabled={!files.length || loading || !targetId}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-blue-300 text-white font-semibold rounded-lg px-6 py-2.5 text-sm min-w-[110px] inline-flex items-center justify-center gap-2">
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="animate-spin h-4 w-4" />
              <span>{progress > 0 && progress < 100 ? `업로드 ${progress}%` : '변환 중...'}</span>
            </span>
          ) : '변환'}
        </button>
        <button onClick={reset} disabled={loading} className="text-slate-600 hover:text-slate-800 border border-slate-300 bg-white hover:bg-slate-50 rounded-lg px-4 py-2 text-sm disabled:opacity-50">
          초기화
        </button>
        <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap cursor-pointer select-none ml-1"
               title="여러 파일이 같은 거래처·같은 날짜면 하나의 출고전표로 합칩니다. 기본은 파일별로 따로 생성.">
          <input type="checkbox" checked={mergeSameDay} disabled={loading}
                 onChange={(e) => setMergeSameDay(e.target.checked)} />
          같은 거래처·날짜 병합
        </label>
        <div className="flex-1" />
        {files.length > 0 && <div className="text-sm text-slate-400 tabular-nums">{files.length}개 파일</div>}
      </div>

      {result && (
        <div className="mt-8">
          {result.download_id ? (
            <div className="rounded-xl bg-gradient-to-br from-emerald-50 to-emerald-100/50 border border-emerald-200 p-5 flex items-center justify-between mb-4 shadow-sm">
              <div>
                <p className="font-semibold text-emerald-900 text-lg">총 {result.total_rows.toLocaleString()}개 행 변환 완료</p>
                <p className="text-sm text-emerald-700/80 mt-1">{okFiles.length}개 파일에서 추출됨</p>
              </div>
              <a href={downloadUrl} download className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg px-5 py-2.5 text-sm inline-flex items-center gap-2 shadow-sm">
                <Download className="w-4 h-4" /> 엑셀 다운로드
              </a>
            </div>
          ) : (
            <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-4 text-amber-900 text-sm">
              변환된 행이 없습니다. 아래 파일별 결과를 확인하세요.
            </div>
          )}

          <ReviewPanel
            committed={committed}
            committing={committing}
            partnerRows={partnerRows} setPartnerRows={setPartnerRows}
            onRenamePartner={renamePartner}
            onRenameItem={renameItem}
            onChangeItemUnit={changeItemUnit}
            itemRows={itemRows} setItemRows={setItemRows}
            itemGroups={itemGroups}
            priceRows={priceRows} setPriceRows={setPriceRows}
            orderRows={orderRows} setOrderRows={setOrderRows}
            itemsMatched={result.items_matched}
            pricesMatched={result.prices_matched}
            onCommit={commitAll}
            groupId={groupId}
            groupName={groupLabel.split(' · ')[0]}
            unmatchedDepts={result.unmatched_depts ?? []}
            onRerunConvert={files.length > 0 ? convert : undefined}
          />

          <div className="space-y-2">
            {result.summary.map((r, i) => {
              const info = STATUS_INFO[r.status] || { cls: 'bg-slate-100 text-slate-700', text: r.status }
              const isOpen = expanded.has(i)
              return (
                <div key={r.file + i} className="rounded-xl bg-white border border-slate-200 overflow-hidden shadow-sm">
                  <button
                    onClick={() => r.status === 'ok' && toggle(i)} disabled={r.status !== 'ok'}
                    className="w-full px-5 py-3.5 flex items-center gap-3 hover:bg-slate-50 text-left disabled:cursor-default disabled:hover:bg-white"
                  >
                    <ChevronRight className={cn('w-4 h-4 text-slate-400 shrink-0 transition-transform', isOpen && 'rotate-90')}
                                  style={r.status !== 'ok' ? { opacity: 0 } : {}} />
                    <span className="flex-1 font-medium text-slate-800 truncate">{r.file}</span>
                    <span className="text-sm text-slate-500 truncate max-w-[180px]">{r.business || (r.status === 'ok' ? '—' : '')}</span>
                    <span className="text-xs text-slate-400 tabular-nums w-20 text-right">{r.date || ''}</span>
                    <span className="text-sm font-semibold text-slate-700 tabular-nums w-14 text-right">{r.line_count > 0 ? `${r.line_count}행` : ''}</span>
                    <span className={cn('inline-block px-2.5 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap', info.cls)}>{info.text}</span>
                  </button>
                  {isOpen && r.lines.length > 0 && (
                    <div className="border-t border-slate-100 bg-slate-50 max-h-96 overflow-y-auto">
                      <table className="w-full text-sm">
                        <thead className="text-xs text-slate-500 uppercase tracking-wide bg-slate-100 sticky top-0">
                          <tr>
                            <th className="text-left px-4 py-2.5 font-medium">품목코드</th>
                            <th className="text-left px-4 py-2.5 font-medium">품명</th>
                            <th className="text-right px-4 py-2.5 font-medium">수량</th>
                            <th className="text-left px-4 py-2.5 font-medium">단위</th>
                            <th className="text-right px-4 py-2.5 font-medium">단가</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.lines.map((l, idx) => (
                            <tr key={idx} className="border-t border-slate-200 hover:bg-white">
                              <td className="px-4 py-2 text-slate-500 text-xs tabular-nums">{l.품목코드 || '—'}</td>
                              <td className="px-4 py-2 text-slate-800">{l.품명}</td>
                              <td className="px-4 py-2 text-right tabular-nums text-slate-700">{formatNum(l.수량 as number)}</td>
                              <td className="px-4 py-2 text-slate-500 text-xs">{l.단위 || ''}</td>
                              <td className="px-4 py-2 text-right tabular-nums text-slate-700">{formatNum(l.단가 as number)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
